/**
 * WalletDetail — W1.5 + W1.7
 *
 * Page-Komponente fuer Route /wallets/:id.
 * Struktur an v2-final/wallet-detail.html angelehnt: Breadcrumb, schlanker
 * Header mit Aktionen (Reimport/Tags/Rename/Remove), 4 KPI-Karten
 * (HNT/SOL/IOT/Rewards 24h), Grid aus Inflows-Chart+Transaktionen (links) und
 * Hotspots-auf-dieser-Wallet + Zuflussquellen (rechts).
 * Fix-Agent (Abgleich wallet-detail.html): der zusaetzliche, im Prototyp
 * nicht vorhandene zweite Block (WalletBalanceCards/TokensSection, MOBILE/DC/
 * Mint-Adressen) wurde entfernt — Duplikat der KPI-Karten oben.
 * Claim-Flow (W1.7): Claim-Buttons fuer Premium-User, Re-Auth-Modal,
 * Confirm-Dialog, Status-Poller.
 */
import { useState, useEffect, useRef } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { Link, useNavigate } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useWalletDetail } from './useWalletDetail'
import { useRefreshWalletBalance } from './useRefreshWalletBalance'
import { useWalletInflows, type WalletInflowEntry } from './useWalletInflows'
import { useWalletHotspots } from './useWalletHotspots'
import { useWalletInflowSplit, type InflowSplitCategory } from './useWalletInflowSplit'
import { WalletTransactions } from './WalletTransactions'
import { WalletEditModal } from './WalletEditModal'
import { disconnectWallet } from './api/walletApi'
import { hashToGradient, truncateAddress } from './walletUtils'
import { hotspotDisplayName } from '../../lib/hotspotDisplayName'
import { useUserStore } from '../../stores/userStore'
import { useReauth } from '../claims/hooks/useReauth'
import { useClaimFlow } from '../claims/hooks/useClaimFlow'
import { ReauthModal } from '../claims/ReauthModal'
import { ClaimConfirmDialog } from '../claims/ClaimConfirmDialog'
import { ClaimStatusPoller } from '../claims/ClaimStatusPoller'
import { DemoGuardedButton } from '../../components/DemoGuardedButton'
import { useReimportWallet } from './useReimportWallet'
import { useTagWalletWithName } from './useTagWalletWithName'
import { BulkTagModal } from './BulkTagModal'
import { fmt, fmtPrice } from '../../lib/fmt'
import { RefreshCw, Tag } from 'lucide-react'
import { useCryptoPrices } from '../../hooks/useCryptoPrices'
import { setPublicPageMeta, publicUrl } from '../../lib/seo'
import './wallet-detail.css'

// --- R5-RI: Confirm-Dialog fuer "Alle Hotspots mit Walletname taggen" (One-Click) ---
// Echter Dialog (kein window.confirm), Muster analog DisconnectConfirmDialog
// in WalletsList.tsx.

type TagWithNameConfirmProps = {
  walletName?: string
  onConfirm: () => void
  onCancel: () => void
  isLoading: boolean
}

function TagWithNameConfirmDialog({ walletName = '', onConfirm, onCancel, isLoading }: TagWithNameConfirmProps) {
  const { t } = useTranslation('wallets')

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={t('tagWithName.dialogLabel')}
      data-testid="tag-with-name-confirm-dialog"
    >
      <div className="card modal-card">
        <p>
          <Trans
            i18nKey="tagWithName.confirmMessage"
            ns="wallets"
            values={{ name: walletName }}
            components={{ strong: <strong /> }}
          />
        </p>
        <div className="wallet-connect-actions">
          <button
            className="btn btn-primary"
            onClick={onConfirm}
            disabled={isLoading}
            data-testid="btn-confirm-tag-with-name"
            aria-busy={isLoading}
          >
            {isLoading ? t('tagWithName.tagging') : t('tagWithName.confirmButton')}
          </button>
          <button
            className="btn btn-ghost"
            onClick={onCancel}
            disabled={isLoading}
            data-testid="btn-cancel-tag-with-name"
          >
            {t('common:actions.cancel')}
          </button>
        </div>
      </div>
    </div>
  )
}

// --- Remove-Confirm-Dialog (P1-Fix: "Remove" fehlte im Prototyp-Abgleich) ---
// Nutzt den bestehenden DELETE /api/v2/wallets/:id-Endpunkt (disconnectWallet,
// bereits produktiv in WalletsList.tsx). Gleiches Dialog-Muster wie dort.

type RemoveConfirmProps = {
  walletName: string
  hotspotCount: number
  onConfirm: () => void
  onCancel: () => void
  isLoading: boolean
}

function RemoveWalletConfirmDialog({ walletName, hotspotCount, onConfirm, onCancel, isLoading }: RemoveConfirmProps) {
  const { t } = useTranslation('wallets')

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={t('remove.dialogLabel')}
      data-testid="remove-wallet-confirm-dialog"
    >
      <div className="card modal-card">
        <p>
          <Trans
            i18nKey="remove.confirmMessage"
            ns="wallets"
            values={{ name: walletName, count: hotspotCount }}
            components={{ strong: <strong /> }}
          />
        </p>
        <div className="wallet-connect-actions">
          <button
            className="btn"
            style={{ background: 'var(--danger)', color: 'white' }}
            onClick={onConfirm}
            disabled={isLoading}
            data-testid="btn-confirm-remove-wallet"
            aria-busy={isLoading}
          >
            {isLoading ? t('remove.removing') : t('remove.confirmButton')}
          </button>
          <button
            className="btn btn-ghost"
            onClick={onCancel}
            disabled={isLoading}
            data-testid="btn-cancel-remove-wallet"
          >
            {t('common:actions.cancel')}
          </button>
        </div>
      </div>
    </div>
  )
}

// --- Breadcrumb ---

function WalletBreadcrumb({ name }: { name: string }) {
  const { t } = useTranslation(['wallets', 'shell'])
  return (
    <nav className="wd-breadcrumb" aria-label="Breadcrumb">
      <Link to="/dashboard">{t('shell:nav.sections.overview')}</Link>
      <span aria-hidden="true">›</span>
      <Link to="/wallets">{t('breadcrumb.myWallets')}</Link>
      <span aria-hidden="true">›</span>
      <span className="wd-breadcrumb-current">{name}</span>
    </nav>
  )
}

// --- Kopierbarer Adress-Chip ---

function CopyableAddress({ address }: { address: string }) {
  const { t } = useTranslation('wallets')
  const [copied, setCopied] = useState(false)

  function handleCopy() {
    navigator.clipboard.writeText(address).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <button
      className="addr-chip addr-chip-copyable"
      onClick={handleCopy}
      aria-label={t('actions.copyAddress', { address })}
      title={t('actions.clickToCopy')}
      style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
      data-testid="wallet-address"
    >
      {address}
      {copied && (
        <span className="copy-feedback muted" aria-live="polite" style={{ marginLeft: 6, fontSize: '0.75rem' }}>
          {t('actions.copied')}
        </span>
      )}
    </button>
  )
}

// --- Header (schlank, Prototyp-Struktur: Name+Pill, Adresse+Count, Aktionen rechts) ---

type WalletHeaderProps = {
  solAddr: string
  name?: string
  owned: boolean
  hotspotCount: number
}

function WalletHeader({ solAddr, name, owned, hotspotCount }: WalletHeaderProps) {
  const { t } = useTranslation('wallets')
  const gradient = hashToGradient(solAddr)
  const displayName = name ?? truncateAddress(solAddr)
  const shortAddr = truncateAddress(solAddr)

  return (
    <div className="row" style={{ gap: 14, alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap' }}>
      <div
        className="identity-img wallet-avatar"
        style={{ background: gradient, width: 48, height: 48, borderRadius: 14, fontSize: 18 }}
        aria-hidden="true"
      >
        <svg width="20" height="20" viewBox="0 0 14 14" fill="none">
          <rect x="1" y="3" width="12" height="9" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
          <path d="M4 3V2C4 1.44772 4.44772 1 5 1H9C9.55228 1 10 1.44772 10 2V3" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </div>
      <div>
        <div className="row" style={{ gap: 8, marginBottom: 4 }}>
          {/* P2-Fix: Prototyp zeigt genau eine Pill ("Own wallet") — die
              zusaetzliche Netzwerk-Pill ("Solana mainnet") entfernt. */}
          {owned && <span className="pill pill-primary">{t('detail.hotWalletPill')}</span>}
        </div>
        <h1 style={{ fontSize: 22 }}>{displayName}</h1>
        <div className="row muted" style={{ gap: 8, fontSize: 12, marginTop: 4, flexWrap: 'wrap' }}>
          <CopyableAddress address={shortAddr} />
          <span>·</span>
          <span>{t('row.hotspots', { count: hotspotCount })}</span>
        </div>
      </div>
    </div>
  )
}

// --- KPI-Karten (4: HNT / SOL / IOT / Rewards 24h) ---

type KpiCardProps = { label: string; value: string; sub?: string }

function KpiCard({ label, value, sub }: KpiCardProps) {
  return (
    <div className="wd-kpi-card">
      <div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
        {label}
      </div>
      <div className="num-lg" style={{ margin: '4px 0' }}>{value}</div>
      {sub && <div className="muted" style={{ fontSize: 12 }}>{sub}</div>}
    </div>
  )
}

type KpiRowProps = {
  hnt: number
  sol: number
  iot: number
  hntPrice: number
  solPrice: number | null
  iotPrice: number
  currency: string
  rewardsLastInflow: number | null
  hotspotCount: number
}

function KpiRowSkeleton() {
  const { t } = useTranslation('wallets')
  return (
    <div className="wd-kpi-grid" aria-busy="true" aria-label={t('loading.balance')}>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="wd-kpi-card skeleton">
          <div className="skeleton-line" style={{ width: 40, height: 11 }} />
          <div className="skeleton-line" style={{ width: 80, height: 22, marginTop: 4 }} />
        </div>
      ))}
    </div>
  )
}

function KpiRow({
  hnt,
  sol,
  iot,
  hntPrice,
  solPrice,
  iotPrice,
  currency,
  rewardsLastInflow,
  hotspotCount,
}: KpiRowProps) {
  const { t } = useTranslation('wallets')
  const solEur = solPrice !== null ? sol * solPrice : null

  return (
    <div className="wd-kpi-grid">
      <KpiCard label={t('kpi.hnt')} value={`${fmt(hnt, 2)} HNT`} sub={`≈ ${fmtPrice(hnt * hntPrice, currency)}`} />
      <KpiCard
        label={t('kpi.sol')}
        value={sol.toFixed(2)}
        sub={solEur !== null ? `≈ ${fmtPrice(solEur, currency)}` : t('row.solNotAvailable')}
      />
      <KpiCard
        label={t('kpi.iotLegacy')}
        value={iot > 0 ? fmt(iot, 2) : '—'}
        sub={iot > 0 ? `≈ ${fmtPrice(iot * iotPrice, currency)}` : t('kpi.noLegacyBalance')}
      />
      <KpiCard
        label={t('kpi.rewardsLastInflow')}
        value={rewardsLastInflow !== null ? `${fmt(rewardsLastInflow, 3)} HNT` : '—'}
        sub={hotspotCount > 0 ? (t('kpi.rewardsFromHotspots', { count: hotspotCount }) as string) : undefined}
      />
    </div>
  )
}

// --- Inflows Chart (echte BE-Daten via useWalletInflows) ---

type InflowsChartProps = {
  inflows: WalletInflowEntry[] | undefined
  isLoading: boolean
}

function InflowsChart({ inflows, isLoading }: InflowsChartProps) {
  const { t } = useTranslation('wallets')

  // P1-Fix: Prototyp zeigt einen einzelnen Verlauf ohne Umschalter (kein
  // HNT/All-tokens-Toggle) — hinter dem Kartentitel steht dort nur ein
  // statisches Token-Label. Ein echter 90-Tage-Balance-Verlauf existiert im
  // Backend nicht (nur `inflows` = 30 Tage, siehe wallets_controller#inflows);
  // ohne neuen Endpunkt (verboten) bleibt HNT-Zufluss die ehrlichste
  // verfuegbare Naeherung, jetzt einheitlich ohne Interaktion dargestellt.
  const data = (inflows ?? []).map((entry, i) => ({
    day: i + 1,
    label: entry.date,
    value: entry.hnt,
  }))

  const height = 200
  const maxValue = data.length > 0 ? Math.max(...data.map(d => d.value), 0.001) : 1

  // Leere Daten — Empty-State-SVG (flache Linie auf Baseline)
  const isEmpty = data.length === 0

  function buildLinePath(pts: { day: number; value: number }[], total: number): string {
    if (pts.length === 0) return ''
    return pts.reduce((acc, d, i) => {
      const x = total > 1 ? (i / (total - 1)) * 600 : 300
      const y = height - (d.value / maxValue) * (height - 20) - 10
      return acc + (i === 0 ? `M ${x},${y}` : ` L ${x},${y}`)
    }, '')
  }

  const linePath = isEmpty ? `M 0,${height - 10} L 600,${height - 10}` : buildLinePath(data, data.length)
  const areaPath = isEmpty
    ? `M 0,${height - 10} L 600,${height - 10} L 600,${height} L 0,${height} Z`
    : linePath + ` L 600,${height} L 0,${height} Z`

  return (
    <div className="card card-pad">
      <div className="spread">
        <h3>{t('chart.inflows30d')}</h3>
        <span className="mono muted" style={{ fontSize: 12 }}>HNT</span>
      </div>
      <div style={{ marginTop: '14px' }}>
        {isLoading ? (
          <div
            aria-busy="true"
            style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}
          >
            {t('loading.data')}
          </div>
        ) : (
          <svg width="100%" height={height} viewBox={`0 0 600 ${height}`} preserveAspectRatio="none">
            {/* Grid lines */}
            {[0, 0.25, 0.5, 0.75, 1].map((tick) => (
              <line
                key={tick}
                x1="0"
                y1={height - (tick * height)}
                x2="600"
                y2={height - (tick * height)}
                stroke="var(--border)"
                strokeWidth="1"
                strokeDasharray="4,4"
              />
            ))}
            {/* Area fill */}
            <path
              d={areaPath}
              fill="url(#inflowsGradient)"
              opacity="0.3"
            />
            {/* Line */}
            <path
              d={linePath}
              fill="none"
              stroke="var(--primary)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <defs>
              <linearGradient id="inflowsGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--primary)" />
                <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
        )}
        {/* X-axis labels */}
        {!isLoading && (
          <div className="row" style={{ justifyContent: 'space-between', marginTop: '8px', fontSize: '10px', color: 'var(--text-muted)' }}>
            <span>{data[0]?.label ?? t('chart.dayTick', { n: 1 })}</span>
            <span>{data[Math.floor(data.length / 2)]?.label ?? t('chart.dayTick', { n: 15 })}</span>
            <span>{data[data.length - 1]?.label ?? t('chart.dayTick', { n: 30 })}</span>
          </div>
        )}
      </div>
    </div>
  )
}

// --- Mini-Sparkline (inline SVG, keine Lib-Abhaengigkeit) ---

function MiniSparkline({ values }: { values: number[] }) {
  // P1-Fix: strukturell immer einen Sparkline-Slot rendern (Design zeigt die
  // Zeile immer mit Sparkline). Ohne Datenpunkte (kein `sparkline_data`-Feld
  // auf GET /api/v2/hotspots — Backend-Luecke, gleiches Verhalten wie
  // HotspotRow.tsx) erscheint der bestehende App-weite Platzhalter statt der
  // Linie, statt die Sparkline komplett wegzulassen.
  if (values.length < 2) return <div className="wd-sparkline-placeholder" aria-hidden="true" />
  const w = 64
  const h = 20
  const max = Math.max(...values, 0.0001)
  const min = Math.min(...values, 0)
  const range = max - min || 1
  const points = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * w
      const y = h - ((v - min) / range) * h
      return `${x},${y}`
    })
    .join(' ')
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      <polyline points={points} fill="none" stroke="var(--primary)" strokeWidth="1.5" />
    </svg>
  )
}

// --- "Hotspots on this wallet" (rechte Spalte) ---

function HotspotsOnWalletPanel({ walletId, hotspotCount }: { walletId: string; hotspotCount: number }) {
  const { t } = useTranslation('wallets')
  // heliumtracker#45: Liste kommt jetzt vom Wallet-Endpunkt (gleiche Grundmenge
  // wie hotspotCount) statt aus der owned+active-Hotspotliste.
  const { hotspots, isLoading } = useWalletHotspots(walletId, hotspotCount)

  return (
    <div className="card card-pad">
      <div className="spread" style={{ marginBottom: 8 }}>
        <h3>{t('hotspotsOnWallet.title')}</h3>
        <Link to="/hotspots" style={{ fontSize: 12, color: 'var(--primary)' }}>
          {t('hotspotsOnWallet.viewAll')}
        </Link>
      </div>
      {hotspotCount === 0 ? (
        <p className="muted" style={{ fontSize: 13 }}>{t('hotspotsOnWallet.empty')}</p>
      ) : isLoading ? (
        <p className="muted" style={{ fontSize: 13 }} aria-busy="true">{t('hotspotsOnWallet.loading')}</p>
      ) : hotspots.length === 0 ? (
        <p className="muted" style={{ fontSize: 13 }}>{t('hotspotsOnWallet.empty')}</p>
      ) : (
        <div>
          {hotspots.map((h) => (
            <div key={h.id} className="wd-hotspot-row">
              <Link
                to="/hotspots/$id"
                params={{ id: h.id }}
                className="ellipsis"
                style={{ color: 'var(--text)', fontWeight: 500 }}
              >
                {hotspotDisplayName(h.name, null, t('hotspotsOnWallet.unnamed'))}
              </Link>
              <MiniSparkline values={h.sparkline} />
              <span className="muted num-sm">{h.hnt24h !== null ? fmt(h.hnt24h, 3) : '—'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// --- "Inflow sources" Split (rechte Spalte) ---

const SPLIT_COLORS: Record<InflowSplitCategory, string> = {
  rewards: 'var(--primary)',
  transfers: 'var(--accent)',
  other: 'var(--surface-3)',
}

function InflowSplitPanel({ walletId, prices }: { walletId: number; prices: { hnt: number | null; iot: number | null; mobile: number | null } }) {
  const { t } = useTranslation('wallets')
  const { entries, isLoading, isEmpty } = useWalletInflowSplit(walletId, prices)

  return (
    <div className="card card-pad">
      <strong style={{ fontSize: 13 }}>{t('inflowSplit.title')}</strong>
      <div style={{ marginTop: 10 }}>
        {isLoading ? (
          <p className="muted" style={{ fontSize: 12 }} aria-busy="true">{t('loading.data')}</p>
        ) : isEmpty ? (
          <p className="muted" style={{ fontSize: 12 }}>{t('inflowSplit.empty')}</p>
        ) : (
          entries.map((entry) => (
            <div key={entry.category} className="wd-split-row">
              <div className="row" style={{ justifyContent: 'space-between', fontSize: 12 }}>
                <span>{t(`inflowSplit.${entry.category}`)}</span>
                <span className="num-sm">{Math.round(entry.pct)}%</span>
              </div>
              <div className="wd-split-bar-track">
                <div
                  className="wd-split-bar-fill"
                  style={{ width: `${entry.pct}%`, background: SPLIT_COLORS[entry.category] }}
                />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

// --- Main Component ---

type Props = {
  walletId: string
  solAddr: string
  name?: string
  owned: boolean
  hotspotCount?: number
  dashboard?: boolean
  notify?: boolean
}

// --- Claim-Button-Section (W1.7, Premium-Only) ---

// CONTRACT-05: Feature-Flag fuer den Token-Claim-Flow. Standardmaessig AUS,
// weil die zugehoerigen Backend-Endpunkte (auth/reauth, wallets/:id/claim)
// nicht existieren. Auf 'true' setzen, sobald das Backend sie bereitstellt.
const TOKEN_CLAIM_ENABLED =
  (import.meta as { env?: Record<string, string> }).env?.VITE_FEATURE_TOKEN_CLAIM === 'true'

const CLAIMABLE_TOKENS = [
  { symbol: 'HNT', balanceKey: 'hnt' as const },
  { symbol: 'IOT', balanceKey: 'iot' as const },
  { symbol: 'MOBILE', balanceKey: 'mobile' as const },
]

type ClaimButtonsSectionProps = {
  balance: { hnt: number; iot: number; mobile: number } | undefined
  onClaim: (tokenType: string, amount: number) => void
}

function ClaimButtonsSection({ balance, onClaim }: ClaimButtonsSectionProps) {
  const { t } = useTranslation('wallets')
  const { user } = useUserStore()
  const isPremium = user?.plan?.toLowerCase() === 'premium'

  if (!balance) return null

  return (
    <section className="claim-buttons-section" aria-label={t('detail.claimToken')}>
      <h3>{t('detail.claimToken')}</h3>
      <div className="claim-buttons-row">
        {CLAIMABLE_TOKENS.map(({ symbol, balanceKey }) => {
          const amount = balance[balanceKey] ?? 0
          const hasBalance = amount > 0

          if (!isPremium) {
            return (
              <div key={symbol} className="claim-btn-wrapper">
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled
                  aria-label={t('detail.premiumRequiredLabel', { symbol })}
                  title={t('detail.premiumRequiredTitle')}
                >
                  {t('detail.claimSymbol', { symbol })}
                </button>
                <span className="hint muted">{t('detail.premiumRequired')}</span>
              </div>
            )
          }

          return (
            <DemoGuardedButton
              key={symbol}
              type="button"
              className="btn btn-primary"
              disabled={!hasBalance}
              onClick={() => onClaim(symbol.toLowerCase(), amount)}
              aria-label={t('detail.claimSymbol', { symbol }) + ` — Balance: ${amount}`}
              title={!hasBalance ? t('detail.noClaimableBalance') : undefined}
            >
              {t('detail.claimSymbol', { symbol })}
            </DemoGuardedButton>
          )
        })}
      </div>
    </section>
  )
}

// --- R5-Review F9: Timer-Handles fuer die selbstloeschenden Ergebnis-Hinweise ---
// Frueher lief ein nackter setTimeout(..., 5000): bei schnellem Seitenwechsel
// feuerte er nach dem Unmount und setzte State auf einer toten Komponente.
// Der Timer haengt jetzt an einer Ref und wird im Unmount-Effect geraeumt.
const RESULT_RESET_MS = 5000

type ResultBanner = { type: 'success' | 'error'; message: string } | null
type ResultTimerRef = { current: ReturnType<typeof setTimeout> | null }

function scheduleResultReset(
  ref: ResultTimerRef,
  setter: (value: ResultBanner) => void,
  delay = RESULT_RESET_MS,
) {
  if (ref.current) clearTimeout(ref.current)
  ref.current = setTimeout(() => {
    ref.current = null
    setter(null)
  }, delay)
}

// --- Main Component ---

export function WalletDetail({ walletId, solAddr, name, owned, hotspotCount = 0, dashboard, notify }: Props) {
  const { t } = useTranslation('wallets')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isEditModalOpen, setEditModalOpen] = useState(false)
  const { balance, isLoading, isError, refetch } = useWalletDetail(Number(walletId))
  const { inflows, isLoading: inflowsLoading } = useWalletInflows(Number(walletId))
  const { prices, currency } = useCryptoPrices()

  // Wallets-public-2026-09: die Seite ist seit 09.2026 wieder öffentlich
  // (analog HotspotDetail). Canonical zeigt immer auf die ID-Form, damit
  // Suchmaschinen nicht mehrere URLs derselben Wallet indexieren.
  useEffect(() => {
    if (!solAddr) return
    const canonical = publicUrl(`/wallets/${walletId}`)
    return setPublicPageMeta({
      title: t('detail.seoTitle', { address: solAddr }),
      description: t('detail.seoDescription', { address: solAddr }),
      canonical,
    })
  }, [solAddr, walletId, t])

  // On-Demand Balance-Refresh
  const { refresh: refreshBalance, isRefreshing } = useRefreshWalletBalance(Number(walletId))
  const autoRefreshTriggered = useRef(false)

  // Code-Review-Fix: die Detailseite ist jetzt oeffentlich verlinkbar und
  // bleibt beim Wechsel von einer Wallet zur naechsten (/wallets/1 -> /wallets/2)
  // als dieselbe Komponenteninstanz gemountet (TanStack Router remountet nicht
  // bei reinem Param-Wechsel) — ohne diesen Reset blieb der Ref nach der ersten
  // Wallet auf `true` haengen und eine zweite, ebenfalls veraltete Balance
  // wurde nie mehr automatisch aufgefrischt.
  useEffect(() => {
    autoRefreshTriggered.current = false
  }, [walletId])

  // Einmalig auto-refresh wenn stale=true (kein Loop).
  // Wallets-public-2026-09: POST .../refresh_balance bleibt authentifiziert
  // (owned-only) — ohne die owned-Bedingung hier würde JEDER Besucher der
  // jetzt öffentlichen Detailseite (auch für fremde Wallets) einen 401-Call
  // gegen einen mutierenden Endpunkt auslösen.
  useEffect(() => {
    if (owned && balance?.stale && !autoRefreshTriggered.current) {
      autoRefreshTriggered.current = true
      refreshBalance()
    }
  }, [owned, balance?.stale, refreshBalance])

  // S10-06: Reimport wallet hotspots
  const { reimport, isPending: isReimporting } = useReimportWallet()
  const [reimportResult, setReimportResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  // S10-07: Bulk tag modal
  const [showBulkTag, setShowBulkTag] = useState(false)

  // R5-RI: One-Click "Alle Hotspots mit Walletname taggen"
  // R5-Review F8: Der Tag-Name wird serverseitig aus dem Wallet-Namen abgeleitet;
  // ohne Namen ist die Aktion nicht ausfuehrbar (BE: 422 wallet_name_missing).
  const walletNameForTag = name?.trim() ? name.trim() : null
  const { tagWithWalletName, isPending: isTagging } = useTagWalletWithName()
  const [showTagWithNameConfirm, setShowTagWithNameConfirm] = useState(false)
  const [tagWithNameResult, setTagWithNameResult] = useState<{ type: 'success' | 'error'; message: string } | null>(
    null,
  )

  // P1-Fix: "Remove" (DELETE /api/v2/wallets/:id, bereits produktiv genutzt
  // in WalletsList.tsx — hier bisher nicht verdrahtet).
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false)
  const [removeError, setRemoveError] = useState<string | null>(null)
  const removeMutation = useMutation({
    mutationFn: () => disconnectWallet(Number(walletId)),
    onSuccess: (result) => {
      if (result.error) {
        setRemoveError(t('error.removeError'))
        return
      }
      setShowRemoveConfirm(false)
      queryClient.invalidateQueries({ queryKey: ['wallets'] })
      navigate({ to: '/wallets' })
    },
    onError: () => setRemoveError(t('error.removeError')),
  })

  // R5-Review F9: Unmount-Cleanup der beiden Ergebnis-Timer.
  const reimportResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tagWithNameResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    // Die Refs werden absichtlich erst in der Cleanup-Funktion gelesen: dort
    // steht der zuletzt gesetzte Timer, genau der soll abgeraeumt werden.
    const timers = [reimportResetTimerRef, tagWithNameResetTimerRef]
    return () => {
      for (const timer of timers) {
        if (timer.current) clearTimeout(timer.current)
      }
    }
  }, [])

  // Claim-Flow (W1.7)
  const [claimToken, setClaimToken] = useState<string>('')
  const [claimMaxAmount, setClaimMaxAmount] = useState<number>(0)
  const [upgradeError, setUpgradeError] = useState(false)

  const { authenticate, getToken, clearToken } = useReauth()
  const {
    state: claimState,
    startReauth,
    reauthSuccess,
    submitClaim,
    claimConfirmed,
    claimFailed,
    claimTimeout,
    reset: resetClaimFlow,
    retryFromFailed,
  } = useClaimFlow()

  function handleClaimStart(tokenType: string, amount: number) {
    setClaimToken(tokenType)
    setClaimMaxAmount(amount)
    setUpgradeError(false)
    startReauth()
  }

  function handleReauthSuccess(token: string) {
    reauthSuccess(token)
  }

  function handleClaimSubmitted(claimId: string) {
    clearToken()
    submitClaim(claimId)
  }

  function handleClaimRetry() {
    clearToken()
    retryFromFailed()
  }

  function handleFlowCancel() {
    clearToken()
    resetClaimFlow()
    setUpgradeError(false)
  }

  function handleUpgradeRequired() {
    setUpgradeError(true)
    handleFlowCancel()
  }

  // S10-06: Reimport hotspots
  // i18n100-1 (Cluster-C-Muster): der Hook wirft Codes, hier wird übersetzt (t()).
  function reimportErrorMessage(code: 'RATE_LIMITED' | 'WALLET_NOT_FOUND' | 'REIMPORT_FAILED'): string {
    if (code === 'RATE_LIMITED') return t('reimport.rateLimited')
    if (code === 'WALLET_NOT_FOUND') return t('error.walletNotFound')
    return t('reimport.error')
  }

  async function handleReimport() {
    const outcome = await reimport(Number(walletId))
    if (outcome.ok) {
      // R5-Review F6: `outcome.message` ist ein unlokalisierter Backend-String
      // ("Re-import has been queued for wallet <addr>.") — er wird bewusst NICHT
      // gerendert. Die Anzeige kommt aus i18n (DE+EN).
      setReimportResult({ type: 'success', message: t('reimport.queued') })
    } else {
      setReimportResult({ type: 'error', message: reimportErrorMessage(outcome.error) })
    }
    scheduleResultReset(reimportResetTimerRef, setReimportResult)
  }

  function handleBulkTagged(count: number) {
    void count
  }

  // R5-RI: One-Click "Alle Hotspots mit Walletname taggen"
  // i18n100-1: der Hook wirft Codes, hier wird übersetzt (t()).
  function tagWithNameErrorMessage(code: 'WALLET_NAME_MISSING' | 'WALLET_NOT_FOUND' | 'TAG_FAILED'): string {
    if (code === 'WALLET_NAME_MISSING') return t('tagWithName.walletNameMissing')
    if (code === 'WALLET_NOT_FOUND') return t('error.walletNotFound')
    return t('tagWithName.error')
  }

  async function handleTagWithWalletName() {
    setShowTagWithNameConfirm(false)
    const outcome = await tagWithWalletName(Number(walletId))
    if (outcome.ok) {
      const message =
        outcome.taggedCount > 0
          ? t('tagWithName.success', { count: outcome.taggedCount, tagName: outcome.tagName })
          : t('tagWithName.alreadyTagged', { tagName: outcome.tagName })
      setTagWithNameResult({ type: 'success', message })
    } else {
      setTagWithNameResult({ type: 'error', message: tagWithNameErrorMessage(outcome.error) })
    }
    scheduleResultReset(tagWithNameResetTimerRef, setTagWithNameResult)
  }

  const displayName = name ?? truncateAddress(solAddr)
  // Review-Befund F-Fixrunde: das ist NICHT "gestern" — jüngster Eintrag der
  // 30-Tage-Inflows kann heute oder (bei Zufluss-Lücke) beliebig weit in der
  // Vergangenheit liegen (kein separater 24h-Endpunkt vorhanden). Eigener
  // Key/Label "letzter Eingang" statt "Gestern".
  const rewardsLastInflow = inflows && inflows.length > 0 ? inflows[inflows.length - 1].hnt : null

  return (
    <main className="wallet-detail-page">
      <WalletBreadcrumb name={displayName} />

      <WalletHeader solAddr={solAddr} name={name} owned={owned} hotspotCount={hotspotCount} />

      {/* Aktionsreihe — nur bei eigener Wallet.
          CONTRACT-04: Der frueher hier stehende WalletDiscoverButton rief
          POST /api/v2/wallets/:id/discover auf — diese Route existiert nicht
          (mehr); die Funktion wurde als POST .../reimport implementiert. Der
          Button lief daher IMMER in den Fehler-Toast, waehrend der direkt
          daneben stehende Reimport-Button dasselbe korrekt erledigt. Der
          Duplikat-Button samt Hook/Komponente wurde entfernt. */}
      {owned && (
        <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* S10-06: Reimport Button */}
          <button
            type="button"
            className="btn btn-sm"
            onClick={handleReimport}
            disabled={isReimporting}
            aria-label={t('detail.reimportLabel')}
            title={t('detail.reimportTitle')}
          >
            <RefreshCw size={14} aria-hidden="true" />
            {isReimporting ? t('detail.reimportingHotspots') : t('actions.reimportHotspots')}
          </button>
          {/* S10-07: Bulk Tag Button — P2-Fix: Prototyp zeigt genau einen
              Tag-Button ("Tag all with wallet name"). Der manuelle
              Auswahl-Dialog bleibt als Fallback erhalten, aber nur solange
              kein Wallet-Name fuer das One-Click-Taggen vorhanden ist —
              sonst stuenden zwei Tag-Buttons nebeneinander, die es im
              Design nicht gibt. */}
          {!walletNameForTag && (
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => setShowBulkTag(true)}
              aria-label={t('detail.tagsWalletLabel')}
              title={t('detail.tagsWalletTitle')}
            >
              <Tag size={14} aria-hidden="true" />
              {t('actions.assignTags')}
            </button>
          )}
          {/* R5-RI: One-Click "Alle Hotspots mit Walletname taggen" — v1-Paritaet,
              bewusst getrennt vom BulkTagModal-Flow (kein Auswahldialog noetig).
              R5-Review F8: ohne Wallet-Namen gibt es keinen Tag-Namen — das BE
              antwortet dann mit 422 wallet_name_missing. Statt eines Buttons mit
              leeren Anfuehrungszeichen ("Alle Hotspots mit „“ taggen") wird er
              gar nicht erst gerendert. */}
          {walletNameForTag && (
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => setShowTagWithNameConfirm(true)}
              disabled={isTagging}
              aria-label={t('tagWithName.buttonLabel')}
              title={t('tagWithName.buttonLabel')}
            >
              <Tag size={14} aria-hidden="true" />
              {isTagging ? t('tagWithName.tagging') : t('tagWithName.button', { name: walletNameForTag })}
            </button>
          )}
          {/* P2-Fix: "Rename" als eigener, benannter Button statt Stift-Icon
              neben dem Titel — matcht die Prototyp-Aktionsreihe. Oeffnet
              dasselbe bestehende WalletEditModal (Name+Dashboard+Notify). */}
          <button
            type="button"
            className="btn btn-sm"
            data-testid="btn-rename-wallet"
            onClick={() => setEditModalOpen(true)}
            aria-label={t('actions.editWallet')}
          >
            {t('actions.rename')}
          </button>
          {/* P1-Fix: "Remove" fehlte komplett — DELETE-Endpunkt existiert
              bereits (disconnectWallet), Aktion war nur nicht verdrahtet. */}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            data-testid="btn-remove-wallet"
            onClick={() => {
              setRemoveError(null)
              setShowRemoveConfirm(true)
            }}
            aria-label={t('remove.buttonLabel', { name: displayName })}
            style={{ color: 'var(--danger)' }}
          >
            {t('actions.removeWallet')}
          </button>
          {reimportResult && (
            <span
              role="status"
              aria-live="polite"
              className={reimportResult.type === 'success' ? 'text-success' : 'text-danger'}
              style={{ fontSize: 12 }}
            >
              {reimportResult.message}
            </span>
          )}
          {tagWithNameResult && (
            <span
              role="status"
              aria-live="polite"
              className={tagWithNameResult.type === 'success' ? 'text-success' : 'text-danger'}
              style={{ fontSize: 12 }}
            >
              {tagWithNameResult.message}
            </span>
          )}
        </div>
      )}

      {/* 4 KPI-Karten: HNT / SOL / IOT / Rewards 24h (Prototyp: ".bal") */}
      {isLoading ? (
        <KpiRowSkeleton />
      ) : isError ? (
        <div className="error-state" role="alert">
          <p>{t('error.loadBalance')}</p>
        </div>
      ) : (
        <KpiRow
          hnt={balance?.hnt ?? 0}
          sol={balance ? balance.sol_lamports / 1e9 : 0}
          iot={balance?.iot ?? 0}
          hntPrice={prices.hnt ?? 0}
          solPrice={prices.sol ?? null}
          iotPrice={prices.iot ?? 0}
          currency={currency}
          rewardsLastInflow={rewardsLastInflow}
          hotspotCount={hotspotCount}
        />
      )}

      {/* Grid: links Chart+Transaktionen, rechts Hotspots-auf-Wallet + Inflow-Split */}
      <div className="wd-grid2">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <InflowsChart inflows={inflows} isLoading={inflowsLoading} />
          <div className="card card-pad">
            <div className="spread" style={{ marginBottom: 8 }}>
              <h3>{t('transactions.listLabel')}</h3>
              {/* Wallets-public-2026-09: Refresh ist ein mutierender,
                  authentifizierter Aufruf (POST .../refresh_balance,
                  owned-only) — auf der jetzt öffentlichen Detailseite darf
                  er nicht für Anonyme/fremde Betrachter erscheinen. */}
              {owned && balance && (
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={refreshBalance}
                  disabled={isRefreshing}
                  aria-label={t('detail.refreshBalance')}
                  data-testid="refresh-balance-btn"
                >
                  <RefreshCw size={13} aria-hidden="true" style={{ marginRight: 5 }} />
                  {isRefreshing ? t('actions.refreshing') : t('actions.refresh')}
                </button>
              )}
            </div>
            <WalletTransactions walletId={walletId} />
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <HotspotsOnWalletPanel walletId={String(walletId)} hotspotCount={hotspotCount} />
          <InflowSplitPanel walletId={Number(walletId)} prices={{ hnt: prices.hnt ?? null, iot: prices.iot ?? null, mobile: prices.mobile ?? null }} />
        </div>
      </div>

      {/* Claim-Buttons (W1.7) — nur bei owned Wallets sinnvoll.
          CONTRACT-05: Der gesamte Claim-Flow zielt auf Endpunkte, die es im
          Backend nicht gibt (POST /api/v2/auth/reauth, POST
          /api/v2/wallets/:id/claim); GET /api/v2/claims/:id existiert zwar,
          liefert aber eine voellig andere Domaene (Auszahlungs-Claim mit
          pending/approved/rejected/paid statt On-Chain-Status +
          tx_signature). Der Flow bricht deshalb bei jedem Versuch ab.
          Bis das Backend-Feature existiert, wird die UI ueber das
          Feature-Flag VITE_FEATURE_TOKEN_CLAIM (Default: aus) verborgen,
          damit Premium-Nutzern kein dauerhaft kaputter Button angeboten
          wird. Der FE-Code bleibt unveraendert bestehen. */}
      {TOKEN_CLAIM_ENABLED && owned && !isLoading && !isError && (
        <ClaimButtonsSection balance={balance} onClaim={handleClaimStart} />
      )}

      {/* Upgrade-Hinweis nach 402 */}
      {upgradeError && (
        <div className="error-state" role="alert">
          <p>
            {t('detail.upgradeRequired')}{' '}
            <Link to="/premium">{t('detail.upgradeLink')}</Link>
          </p>
        </div>
      )}

      {/* Claim-Status-Anzeige im polling/success/failed/timeout-State */}
      {(claimState.phase === 'polling' ||
        claimState.phase === 'success' ||
        claimState.phase === 'failed' ||
        claimState.phase === 'timeout') && (
        <section className="claim-status-section" aria-label={t('detail.claimStatusLabel')}>
          {claimState.phase === 'polling' && (
            <ClaimStatusPoller
              claimId={claimState.claimId}
              onConfirmed={claimConfirmed}
              onFailed={claimFailed}
              onTimeout={() => claimTimeout(claimState.claimId)}
              onRetry={handleClaimRetry}
              onRefetchBalance={() => refetch?.()}
            />
          )}
          {claimState.phase === 'success' && (
            <div className="claim-status-success" role="status">
              <span className="pill-success">{t('detail.claimSuccessful')}</span>
              <p>
                {t('detail.txPrefix')}{' '}
                <a
                  href={`https://solscan.io/tx/${claimState.txSignature}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {`${claimState.txSignature.slice(0, 8)}...${claimState.txSignature.slice(-8)}`}
                </a>
              </p>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={resetClaimFlow}
              >
                {t('detail.closeSuccess')}
              </button>
            </div>
          )}
          {claimState.phase === 'failed' && (
            <div className="claim-status-failed" role="status">
              <span className="pill-danger">{t('detail.claimFailed')}</span>
              <p>{claimState.reason}</p>
              <div className="failed-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleClaimRetry}
                >
                  {t('detail.retryAgain')}
                </button>
                <a
                  href="https://discord.gg/heliumtracker"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-ghost"
                >
                  {t('detail.contactSupport')}
                </a>
              </div>
            </div>
          )}
          {claimState.phase === 'timeout' && (
            <div className="claim-status-timeout" role="status">
              <p>{t('detail.claimTimeout')}</p>
              <p>
                <strong>{t('detail.claimId')}</strong>{' '}
                <button
                  type="button"
                  className="copy-btn"
                  onClick={() =>
                    navigator.clipboard.writeText(claimState.claimId)
                  }
                >
                  {claimState.claimId}
                </button>
              </p>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => submitClaim(claimState.claimId)}
              >
                {t('detail.checkStatus')}
              </button>
            </div>
          )}
        </section>
      )}

      {/* Re-Auth-Modal (W1.7) */}
      {claimState.phase === 'reauth' && (
        <ReauthModal
          authenticate={authenticate}
          getToken={getToken}
          onSuccess={handleReauthSuccess}
          onCancel={handleFlowCancel}
        />
      )}

      {/* Claim-Confirm-Dialog (W1.7) */}
      {claimState.phase === 'confirm' && (
        <ClaimConfirmDialog
          walletId={walletId}
          tokenType={claimToken}
          maxAmount={claimMaxAmount}
          reauthToken={claimState.reauthToken}
          onSubmitted={handleClaimSubmitted}
          onCancel={handleFlowCancel}
          onReauthExpired={() => {
            clearToken()
            startReauth()
          }}
          onUpgradeRequired={handleUpgradeRequired}
        />
      )}

      {/* S10-07: Bulk Tag Modal */}
      {showBulkTag && (
        <BulkTagModal
          walletId={Number(walletId)}
          walletName={name}
          onClose={() => setShowBulkTag(false)}
          onTagged={handleBulkTagged}
        />
      )}

      {/* R5-RI: Confirm-Dialog fuer One-Click Tag-with-Wallet-Name */}
      {showTagWithNameConfirm && (
        <TagWithNameConfirmDialog
          walletName={walletNameForTag ?? ''}
          isLoading={isTagging}
          onConfirm={handleTagWithWalletName}
          onCancel={() => setShowTagWithNameConfirm(false)}
        />
      )}

      {/* P1-Fix: Confirm-Dialog fuer "Remove" */}
      {showRemoveConfirm && (
        <RemoveWalletConfirmDialog
          walletName={displayName}
          hotspotCount={hotspotCount}
          isLoading={removeMutation.isPending}
          onConfirm={() => removeMutation.mutate()}
          onCancel={() => setShowRemoveConfirm(false)}
        />
      )}
      {removeError && (
        <p role="alert" className="text-danger" style={{ fontSize: 12, marginTop: 8 }}>
          {removeError}
        </p>
      )}

      {isEditModalOpen && (
        <WalletEditModal
          walletId={Number(walletId)}
          initialName={name}
          initialDashboard={dashboard}
          initialNotify={notify}
          onClose={() => setEditModalOpen(false)}
        />
      )}
    </main>
  )
}
